import { create } from 'zustand';
import { io, type Socket } from 'socket.io-client';
import type { Ack, RetroSnapshot, VoteTarget } from '@retro/shared';
import { voteTargetKey } from '@retro/shared';
import { toast } from './toast.js';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

type RetroStore = {
  retroId: string | null;
  snapshot: RetroSnapshot | null;
  status: ConnectionStatus;
  fatalError: string | null;
  /**
   * Votes the user has just changed but which the server has not confirmed yet.
   * Overlaid on the snapshot so the stepper responds instantly on a slow link.
   */
  pendingVotes: Record<string, number>;

  connect(retroId: string): void;
  disconnect(): void;
  send<T = unknown>(event: string, payload?: unknown): Promise<T | null>;
  setVote(target: VoteTarget, count: number): Promise<void>;
};

let socket: Socket | null = null;

export const useRetroStore = create<RetroStore>((set, get) => ({
  retroId: null,
  snapshot: null,
  status: 'idle',
  fatalError: null,
  pendingVotes: {},

  connect(retroId) {
    if (socket && get().retroId === retroId) return;

    socket?.close();
    set({ retroId, status: 'connecting', snapshot: null, fatalError: null, pendingVotes: {} });

    socket = io({
      auth: { retroId },
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socket.on('connect', () => set({ status: 'connected' }));

    socket.on('retro:state', (snapshot: RetroSnapshot) => {
      // The server is authoritative; drop any optimistic vote overlay.
      set({ snapshot, status: 'connected', pendingVotes: {} });
    });

    socket.on('disconnect', (reason) => {
      // An explicit local close is not an error worth showing.
      if (reason === 'io client disconnect') return;
      set({ status: 'reconnecting' });
    });

    socket.on('connect_error', (error) => {
      const message = error.message || 'Could not join this retrospective.';
      // Auth failures will not fix themselves by retrying.
      if (/not joined|Missing retroId|Could not join/i.test(message)) {
        socket?.close();
        // The guard in connect() early-returns while `socket` is non-null, so
        // a dead socket left here would swallow the next connect for the same
        // retro. Null it so a retry (or a session arriving mid-mount) starts
        // fresh.
        socket = null;
        set({ status: 'error', fatalError: message });
        return;
      }
      set({ status: 'reconnecting' });
    });
  },

  disconnect() {
    socket?.close();
    socket = null;
    set({ retroId: null, snapshot: null, status: 'idle', pendingVotes: {} });
  },

  async send<T>(event: string, payload?: unknown): Promise<T | null> {
    const active = socket;
    if (!active) return null;

    const ack = await new Promise<Ack<T>>((resolve) => {
      // If the server never answers, fail soft rather than hanging a button.
      const timer = setTimeout(
        () => resolve({ ok: false, error: { code: 'INTERNAL', message: 'The server did not respond.' } }),
        10_000,
      );
      active.emit(event, payload ?? {}, (response: Ack<T>) => {
        clearTimeout(timer);
        resolve(response);
      });
    });

    if (!ack.ok) {
      toast.error(ack.error.message);
      // A stale phase means our view is behind; ask for the truth.
      if (ack.error.code === 'CONFLICT') active.emit('retro:sync');
      return null;
    }

    return (ack.data ?? null) as T | null;
  },

  async setVote(target, count) {
    const key = voteTargetKey(target);
    set((state) => ({ pendingVotes: { ...state.pendingVotes, [key]: count } }));

    const result = await get().send('vote:set', { target, count });

    if (result === null) {
      // Rejected: discard the optimistic value so the real count shows again.
      set((state) => {
        const next = { ...state.pendingVotes };
        delete next[key];
        return { pendingVotes: next };
      });
    }
  },
}));

/** My vote count for a target, including any not-yet-confirmed change. */
export function useMyVotes(target: VoteTarget): number {
  const key = voteTargetKey(target);
  return useRetroStore((state) => state.pendingVotes[key] ?? state.snapshot?.votes.mine[key] ?? 0);
}

/** Votes used, accounting for optimistic changes, so the budget label stays honest. */
export function useVotesUsed(): number {
  return useRetroStore((state) => {
    const { snapshot, pendingVotes } = state;
    if (!snapshot) return 0;
    const merged = { ...snapshot.votes.mine, ...pendingVotes };
    return Object.values(merged).reduce((sum, count) => sum + count, 0);
  });
}
